import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  LayoutDashboard,
  Briefcase,
  Clock,
  Users,
  CheckSquare,
  BarChart3,
  Sparkles,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Bell,
  ChevronLeft,
  CheckCheck,
  Layers,
  Megaphone,
  X as XIcon,
  Info,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { AdminTour } from './AdminTour';

const CHANGELOG = [
  {
    version: '1.6.0',
    date: 'Jul 2026',
    changes: [
      { type: 'New', text: 'Service Catalog — Tasks added under each vertical; Verticals and Work Types are now linked (each work type belongs to a vertical)' },
      { type: 'New', text: 'Projects — Non-Billable toggle for internal projects; badge shown in grid and list views' },
      { type: 'New', text: 'Projects — Budgeted Hours auto-calculated from the sum of service vertical hours when services are configured' },
      { type: 'New', text: 'What\'s New popup — automatically shown to every user on first login after a new release; blue dot on version number until dismissed' },
      { type: 'Improvement', text: 'Projects list view — columns no longer collapse on tablet screens; full names visible at all sizes' },
      { type: 'Improvement', text: 'Projects — progress bar hidden when no budget is set; no more false "over budget" warning on unbudgeted projects' },
      { type: 'Fix', text: 'Time Entry — Recall button no longer appears after submitting without making any edits' },
      { type: 'Fix', text: 'Time Entry — allocation limit check now correctly allows logging hours up to the exact allocated amount (was blocking at 100% instead of over 100%)' },
      { type: 'Fix', text: 'Time Entry — description save now works for admin and super-admin users on approved timesheets' },
      { type: 'Fix', text: 'Approvals — hours display no longer shows floating point noise (e.g. 13.799999h → 13.8h)' },
      { type: 'Fix', text: 'Approvals — billable hours breakdown now correctly counts entries where billable flag is unset (treated as billable, matching backend)' },
    ],
  },
  {
    version: '1.5.0',
    date: 'Jun 2026',
    changes: [
      { type: 'New', text: 'Manager Time Entry — You / Team tab to view your own timesheet and a read-only grid of all direct reports side by side' },
      { type: 'New', text: 'Approvals member filter — filter Pending / Approved / Rejected timesheets by individual team member' },
      { type: 'New', text: 'Weekly reminder email — managers receive a combined email every Monday listing all unapproved timesheets older than the configured threshold' },
      { type: 'New', text: 'Admin setting: Timesheet Approval Reminder — configure days after submission and exact send time (e.g. 10:30 AM)' },
      { type: 'Improvement', text: 'Finance & Leadership Viewer roles no longer see Time Entry and Projects in the sidebar' },
      { type: 'Fix', text: 'Hours validation — backend now rejects negative, zero, or entries exceeding 24 hours' },
      { type: 'Fix', text: 'Finance Viewer Reports page no longer shows "Failed to fetch" error' },
      { type: 'Fix', text: 'Calendar navigation arrows now visible in dark mode' },
    ],
  },
  {
    version: '1.4.0',
    date: 'May 2026',
    changes: [
      { type: 'New', text: 'Onboarding tour — each page guides you through its features on first visit' },
      { type: 'New', text: 'Role-based tour steps — admins, report viewers, and team members see steps relevant to their role' },
      { type: 'New', text: 'Restart tour anytime via the ? button in the sidebar footer' },
    ],
  },
  {
    version: '1.3.0',
    date: 'Apr 2026',
    changes: [
      { type: 'New', text: 'Retainer Flag on Projects — Fixed Price, Monthly Retainer, Time & Material project types' },
      { type: 'New', text: 'Monthly burn rate scoped to current calendar month for retainer projects' },
    ],
  },
  {
    version: '1.2.0',
    date: 'Apr 2026',
    changes: [
      { type: 'New', text: 'Work Notes & Blockers on time entries' },
      { type: 'New', text: 'Blockers aggregated by project in Reports' },
      { type: 'New', text: 'Blockers visible in Approvals table and Edit Entry dialog' },
    ],
  },
  {
    version: '1.1.0',
    date: 'Apr 2026',
    changes: [
      { type: 'New', text: 'Hierarchical Work Tagging — Service Verticals and Work Types per project' },
      { type: 'New', text: 'Service Catalog admin page to manage verticals and work types' },
      { type: 'New', text: 'Cascading vertical → work type dropdowns in time entry' },
    ],
  },
  {
    version: '1.0.0',
    date: 'Mar 2026',
    changes: [
      { type: 'New', text: 'Initial release — Time tracking, project management, approvals workflow' },
      { type: 'New', text: 'AI Insights, Reports, Team management, Notifications' },
      { type: 'New', text: 'Dark / Light mode, role-based access control' },
    ],
  },
];

const typeBadgeClass = {
  New: 'bg-green-500/15 text-green-600',
  Fix: 'bg-red-500/15 text-red-600',
  Improvement: 'bg-blue-500/15 text-blue-600',
};

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isManager, isViewer, canViewReports, canApprove, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = React.useState(0);
  const [unreadNotifications, setUnreadNotifications] = React.useState(0);
  const [notifications, setNotifications] = React.useState([]);
  const [notifOpenDesktop, setNotifOpenDesktop] = React.useState(false);
  const [notifOpenMobile, setNotifOpenMobile] = React.useState(false);
  const CURRENT_VERSION = CHANGELOG[0].version;
  const [changelogOpen, setChangelogOpen] = React.useState(false);
  const [versionUnseen, setVersionUnseen] = React.useState(false);
  const [announcement, setAnnouncement] = React.useState(null);
  const [announcementDismissed, setAnnouncementDismissed] = React.useState(null);
  const [announceOpen, setAnnounceOpen] = React.useState(false);
  const [announceMsg, setAnnounceMsg] = React.useState('');
  const [announceType, setAnnounceType] = React.useState('info');
  const contentRef = React.useRef(null);

  React.useEffect(() => {
    if (!user || !isAdmin) return;
    const key = `workintel_last_seen_version_${user.id}`;
    const lastSeen = localStorage.getItem(key);
    if (lastSeen !== CURRENT_VERSION) {
      setVersionUnseen(true);
      setChangelogOpen(true);
    }
  }, [user, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangelogClose = (open) => {
    if (!open) {
      const key = `workintel_last_seen_version_${user?.id}`;
      localStorage.setItem(key, CURRENT_VERSION);
      setVersionUnseen(false);
    }
    setChangelogOpen(open);
  };

  React.useEffect(() => {
    fetchCounts();
    fetchAnnouncement();
    if (contentRef.current) {
      contentRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCounts = async () => {
    try {
      if (canApprove) {
        const res = await api.get('/timesheets?status=submitted');
        setPendingApprovals(res.data.length);
      }
      const notifRes = await api.get('/notifications?unread_only=true');
      setUnreadNotifications(notifRes.data.length);
    } catch (err) {
      console.error('Failed to fetch counts');
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (err) {
      console.error('Failed to fetch notifications');
    }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setUnreadNotifications(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Failed to mark notifications as read');
    }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await api.put(`/notifications/${notif.id}/read`);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
        setUnreadNotifications(prev => Math.max(0, prev - 1));
      } catch (err) { /* ignore */ }
    }
    setNotifOpenDesktop(false);
    setNotifOpenMobile(false);
    if (notif.link) navigate(notif.link);
  };

  const fetchAnnouncement = async () => {
    try {
      const res = await api.get('/announcements/active');
      setAnnouncement(res.data);
    } catch (err) { /* ignore */ }
  };

  const postAnnouncement = async () => {
    if (!announceMsg.trim()) return;
    try {
      const res = await api.post('/announcements', { message: announceMsg.trim(), type: announceType });
      setAnnouncement(res.data);
      setAnnouncementDismissed(null);
      setAnnounceMsg('');
      setAnnounceOpen(false);
    } catch (err) { /* ignore */ }
  };

  const removeAnnouncement = async () => {
    if (!announcement) return;
    try {
      await api.delete(`/announcements/${announcement.id}`);
      setAnnouncement(null);
    } catch (err) { /* ignore */ }
  };

  const handleBellOpenDesktop = (open) => {
    setNotifOpenDesktop(open);
    if (open) fetchNotifications();
  };
  const handleBellOpenMobile = (open) => {
    setNotifOpenMobile(open);
    if (open) fetchNotifications();
  };

  const formatTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const [tourTrigger, setTourTrigger] = React.useState(0);

  const startTour = React.useCallback(() => {
    setSidebarOpen(true);
    setTourTrigger(n => n + 1);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
    { path: '/projects', label: 'Projects', icon: Briefcase, show: !isViewer },
    { path: '/time-entry', label: 'Time Entry', icon: Clock, show: !isViewer },
    { path: '/approvals', label: 'Approvals', icon: CheckSquare, show: canApprove, badge: pendingApprovals },
    { path: '/team', label: 'Team', icon: Users, show: isAdmin },
    { path: '/reports', label: 'Reports', icon: BarChart3, show: canViewReports },
    { path: '/ai-insights', label: 'AI Insights', icon: Sparkles, show: canViewReports },
    { path: '/service-catalog', label: 'Service Catalog', icon: Layers, show: isAdmin },
    { path: '/settings', label: 'Settings', icon: Settings, show: true },
  ];

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col border-r border-border bg-card transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-16'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={`h-16 flex items-center border-b border-border ${sidebarOpen ? 'justify-between px-4' : 'justify-center'}`}>
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="font-bold tracking-tight">WorkIntel</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
                className="h-8 w-8"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity"
              title="Expand sidebar"
            >
              <Clock className="w-5 h-5 text-primary-foreground" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {navItems.filter(item => item.show).map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''} ${sidebarOpen ? '' : 'justify-center px-0'}`
                }
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {item.badge > 0 && (
                      <Badge className="bg-primary text-primary-foreground text-xs">{item.badge}</Badge>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="p-4 border-t border-border">
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                  {user?.profile_image ? (
                    <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{user?.first_name} {user?.last_name}</div>
                  <div className="text-xs text-muted-foreground truncate capitalize">{user?.role?.replace('_', ' ')}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={handleLogout} className="h-8 w-8" data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
              {/* Arismeta credit */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <a
                    href="https://arismeta.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    <span>Created by</span>
                    <span className="font-medium">Arismeta</span>
                  </a>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={startTour}
                      title="Restart page tour"
                      className="text-muted-foreground/60 hover:text-primary transition-colors"
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setChangelogOpen(true)} className="relative text-xs font-mono text-muted-foreground/60 hover:text-primary transition-colors cursor-pointer">
                      v{CURRENT_VERSION}
                      {versionUnseen && <span className="absolute -top-1 -right-1.5 w-1.5 h-1.5 rounded-full bg-blue-500" />}
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Button variant="ghost" size="icon" onClick={handleLogout} className="w-full" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>
      </aside>

      {/* Mobile Header & Menu */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="h-14 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold">WorkIntel</span>
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <Button variant="ghost" size="icon" onClick={() => setAnnounceOpen(true)} className="relative">
                <Megaphone className="w-5 h-5" />
                {announcement && <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />}
              </Button>
            )}
            <Popover open={notifOpenMobile} onOpenChange={handleBellOpenMobile}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className={`relative ${unreadNotifications > 0 ? 'text-primary' : ''}`}>
                  <Bell className="w-5 h-5" />
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-50" />
                      <span className="relative inline-flex items-center justify-center rounded-full h-4 w-4 bg-primary text-primary-foreground text-[10px] font-bold">
                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                      </span>
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <span className="font-semibold text-sm">Notifications</span>
                  {unreadNotifications > 0 && (
                    <button onClick={markAllRead} className="text-xs text-primary flex items-center gap-1 hover:underline">
                      <CheckCheck className="w-3 h-3" /> Mark all read
                    </button>
                  )}
                </div>
                <ScrollArea className="h-80">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications</div>
                  ) : (
                    notifications.map((notif) => (
                      <button key={notif.id} onClick={() => handleNotificationClick(notif)}
                        className={`w-full text-left px-4 py-3 border-b border-border transition-colors relative ${!notif.is_read ? 'bg-primary/8 hover:bg-primary/12 border-l-2 border-l-primary pl-3.5' : 'hover:bg-muted/50'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${!notif.is_read ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}`}>{notif.title}</p>
                            <p className={`text-xs mt-0.5 line-clamp-2 ${!notif.is_read ? 'text-foreground/70' : 'text-muted-foreground'}`}>{notif.message}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTimeAgo(notif.created_at)}</span>
                            {!notif.is_read && (
                              <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-card border-b border-border p-4 shadow-lg">
            <nav className="space-y-1">
              {navItems.filter(item => item.show).map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  {item.badge > 0 && (
                    <Badge className="bg-primary text-primary-foreground text-xs">{item.badge}</Badge>
                  )}
                </NavLink>
              ))}
              <button onClick={handleLogout} className="sidebar-link w-full text-destructive">
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </nav>
            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
              <a
                href="https://arismeta.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Created by <span className="font-medium">Arismeta</span>
              </a>
              <button onClick={() => setChangelogOpen(true)} className="relative text-xs font-mono text-muted-foreground/60 hover:text-primary transition-colors cursor-pointer">
                v{CURRENT_VERSION}
                {versionUnseen && <span className="absolute -top-1 -right-1.5 w-1.5 h-1.5 rounded-full bg-blue-500" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 flex-shrink-0 items-center justify-between px-6 border-b border-border bg-card">
          <div>
            {/* Breadcrumb could go here */}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="ghost" size="icon" onClick={() => setAnnounceOpen(true)} className="relative rounded-lg" title="Post announcement" data-tour="announce-btn">
                <Megaphone className="w-5 h-5" />
                {announcement && <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />}
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-lg" data-testid="theme-toggle" data-tour="theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Popover open={notifOpenDesktop} onOpenChange={handleBellOpenDesktop}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-tour="notif-btn"
                  className={`relative rounded-lg transition-all ${unreadNotifications > 0 ? 'text-primary' : ''}`}
                >
                  <Bell className="w-5 h-5" />
                  {unreadNotifications > 0 && (
                    <>
                      {/* outer ping ring */}
                      <span className="absolute -top-1 -right-1 flex h-4 w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-50" />
                        <span className="relative inline-flex items-center justify-center rounded-full h-4 w-4 bg-primary text-primary-foreground text-[10px] font-bold">
                          {unreadNotifications > 9 ? '9+' : unreadNotifications}
                        </span>
                      </span>
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <span className="font-semibold text-sm">Notifications</span>
                  {unreadNotifications > 0 && (
                    <button onClick={markAllRead} className="text-xs text-primary flex items-center gap-1 hover:underline">
                      <CheckCheck className="w-3 h-3" /> Mark all read
                    </button>
                  )}
                </div>
                <ScrollArea className="h-80">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications</div>
                  ) : (
                    notifications.map((notif) => (
                      <button
                        key={notif.id}
                        onClick={() => handleNotificationClick(notif)}
                        className={`w-full text-left px-4 py-3 border-b border-border transition-colors relative ${
                          !notif.is_read
                            ? 'bg-primary/8 hover:bg-primary/12 border-l-2 border-l-primary pl-3.5'
                            : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${!notif.is_read ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}`}>
                              {notif.title}
                            </p>
                            <p className={`text-xs mt-0.5 line-clamp-2 ${!notif.is_read ? 'text-foreground/70' : 'text-muted-foreground'}`}>{notif.message}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTimeAgo(notif.created_at)}</span>
                            {!notif.is_read && (
                              <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-border hover:ring-primary/50 transition-all outline-none flex-shrink-0">
                  {user?.profile_image ? (
                    <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary select-none">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="p-0">
                  <div className="flex items-center gap-3 px-3 py-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-border flex-shrink-0">
                      {user?.profile_image ? (
                        <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary select-none">
                          {user?.first_name?.[0]}{user?.last_name?.[0]}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{user?.first_name} {user?.last_name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
                      {user?.employee_id && (
                        <p className="text-xs text-muted-foreground/60 font-mono mt-0.5">{user.employee_id}</p>
                      )}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div ref={contentRef} id="tour-scroll-container" className="flex-1 pt-14 lg:pt-0 overflow-auto bg-background">

          {/* Announcement Bar */}
          {announcement && announcementDismissed !== announcement.id && (() => {
            const styles = {
              info:    { wrap: 'from-blue-500/15 to-blue-500/5 border-blue-500/40',   text: 'text-blue-700 dark:text-blue-300',   badge: 'bg-blue-500 shadow-blue-500/40',   icon: <Info className="w-4 h-4" /> },
              warning: { wrap: 'from-amber-500/15 to-amber-500/5 border-amber-500/40', text: 'text-amber-700 dark:text-amber-300', badge: 'bg-amber-500 shadow-amber-500/40', icon: <AlertTriangle className="w-4 h-4" /> },
              success: { wrap: 'from-green-500/15 to-green-500/5 border-green-500/40', text: 'text-green-700 dark:text-green-300', badge: 'bg-green-500 shadow-green-500/40', icon: <CheckCircle2 className="w-4 h-4" /> },
              error:   { wrap: 'from-red-500/15 to-red-500/5 border-red-500/40',       text: 'text-red-700 dark:text-red-300',     badge: 'bg-red-500 shadow-red-500/40',     icon: <AlertCircle className="w-4 h-4" /> },
            };
            const s = styles[announcement.type] || styles.info;
            return (
              <div className={`relative flex items-center gap-3 px-5 py-2.5 border-b bg-gradient-to-r ${s.wrap} overflow-hidden`}>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_3s_ease-in-out_infinite]" />
                <span className={`flex items-center justify-center w-7 h-7 rounded-full text-white flex-shrink-0 shadow-md ${s.badge}`}>
                  {s.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-semibold ${s.text}`}>{announcement.message}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isAdmin && (
                    <button onClick={removeAnnouncement} className={`text-xs font-medium px-2.5 py-1 rounded-full border ${s.text} border-current opacity-60 hover:opacity-100 transition-opacity`}>
                      Remove
                    </button>
                  )}
                  <button onClick={() => setAnnouncementDismissed(announcement.id)} className={`w-6 h-6 rounded-full flex items-center justify-center ${s.text} opacity-50 hover:opacity-100 hover:bg-black/10 transition-all`}>
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })()}

          <div className="p-6 lg:p-8">
            {children}
          </div>
        </div>
      </main>

      <AdminTour pathname={location.pathname} manualTrigger={tourTrigger} isAdmin={isAdmin} isManager={isManager} canViewReports={canViewReports} />

      {/* Announcement Create Dialog */}
      <Dialog open={announceOpen} onOpenChange={setAnnounceOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          {/* Dialog header with gradient */}
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 px-6 py-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-base">Post Announcement</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Visible to all team members instantly</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Type selector */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">Announcement Type</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { t: 'info',    label: 'Info',    icon: <Info className="w-4 h-4" />,          active: 'bg-blue-500 text-white shadow-lg shadow-blue-500/30 border-blue-500' },
                  { t: 'warning', label: 'Warning', icon: <AlertTriangle className="w-4 h-4" />, active: 'bg-amber-500 text-white shadow-lg shadow-amber-500/30 border-amber-500' },
                  { t: 'success', label: 'Success', icon: <CheckCircle2 className="w-4 h-4" />, active: 'bg-green-500 text-white shadow-lg shadow-green-500/30 border-green-500' },
                  { t: 'error',   label: 'Urgent',  icon: <AlertCircle className="w-4 h-4" />,  active: 'bg-red-500 text-white shadow-lg shadow-red-500/30 border-red-500' },
                ].map(({ t, label, icon, active }) => (
                  <button
                    key={t}
                    onClick={() => setAnnounceType(t)}
                    className={`flex flex-col items-center gap-1.5 py-2.5 px-2 rounded-xl text-xs font-medium border transition-all ${
                      announceType === t ? active : 'bg-muted/50 border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {icon}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">Message</label>
              <textarea
                className="w-full text-sm border border-border rounded-xl p-3 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 placeholder:text-muted-foreground/40 transition-all break-words"
                rows={3}
                maxLength={200}
                placeholder="e.g. Timesheets are due by Friday 5pm. Please ensure all hours are submitted."
                value={announceMsg}
                onChange={e => setAnnounceMsg(e.target.value)}
                autoFocus
              />
              <div className="flex items-center justify-between mt-1.5">
                {announcement
                  ? <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Replaces current announcement</p>
                  : <span />
                }
                <span className="text-xs text-muted-foreground/60">{announceMsg.length}/200</span>
              </div>
            </div>

            {/* Preview */}
            {announceMsg.trim() && (() => {
              const prev = {
                info:    'from-blue-500/15 to-blue-500/5 border-blue-500/40 text-blue-700 dark:text-blue-300 bg-blue-500',
                warning: 'from-amber-500/15 to-amber-500/5 border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500',
                success: 'from-green-500/15 to-green-500/5 border-green-500/40 text-green-700 dark:text-green-300 bg-green-500',
                error:   'from-red-500/15 to-red-500/5 border-red-500/40 text-red-700 dark:text-red-300 bg-red-500',
              }[announceType] || '';
              const [from,to,bdr,txt,bg] = prev.split(' ');
              return (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">Preview</label>
                  <div className={`flex items-start gap-3 px-4 py-2.5 rounded-xl border bg-gradient-to-r overflow-hidden ${from} ${to} ${bdr}`}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white flex-shrink-0 mt-0.5 ${bg}`}>
                      {{ info: <Info className="w-3.5 h-3.5" />, warning: <AlertTriangle className="w-3.5 h-3.5" />, success: <CheckCircle2 className="w-3.5 h-3.5" />, error: <AlertCircle className="w-3.5 h-3.5" /> }[announceType]}
                    </span>
                    <span className={`text-xs font-semibold break-all min-w-0 ${txt}`}>{announceMsg}</span>
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setAnnounceOpen(false)}>Cancel</Button>
              <Button onClick={postAnnouncement} disabled={!announceMsg.trim()} className="gap-2">
                <Megaphone className="w-4 h-4" /> Post to All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Changelog Modal */}
      <Dialog open={changelogOpen} onOpenChange={handleChangelogClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              What's New
              <span className="text-xs font-mono text-muted-foreground font-normal">WorkIntel</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-2">
            {CHANGELOG.map((entry, idx) => (
              <div key={entry.version}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-bold text-sm font-mono">{entry.version}</span>
                  <span className="text-xs text-muted-foreground">{entry.date}</span>
                  {idx === 0 && (
                    <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded font-medium">Latest</span>
                  )}
                </div>
                <ul className="space-y-2">
                  {entry.changes.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 mt-0.5 ${typeBadgeClass[c.type]}`}>{c.type}</span>
                      <span className="text-muted-foreground">{c.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-b border-border mt-4" />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
